import { useState, useEffect, useCallback, useRef } from "react";
import { getFuturesContractSize } from "../shared/getContractSize";

/**
 * Gate Futures (USDT contracts) — WebSocket v4
 * symbol: "ETH_USDT"
 * channels:
 *   - futures.obu (order book v2)
 *   - futures.tickers
 * WS: wss://fx-ws.gateio.ws/v4/ws/usdt
 */
export const useGateFutures = (symbol) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError]             = useState(null);
  const [orderBook, setOrderBook]     = useState({ asks: [], bids: [] });
  const [tickerData, setTickerData]   = useState(null);
  const [fundingRate, setFundingRate] = useState(0);

  const wsRef = useRef(null);
  const orderBookRef = useRef({ asks: [], bids: [] });
  const hasInitialBook = useRef(false);
  const animationFrameRef = useRef(null);
  const deltaBufferRef = useRef([]);
  const lastUpdateId = useRef(null);
  const contractSizeRef = useRef(1); // 👈 quanto_multiplier

  const scaleBook = useCallback((ob) => {
    const k = contractSizeRef.current || 1;
    const mul = (arr) => arr.map(([p, q]) => [p, String(Number(q) * k)]);
    return { asks: mul(ob.asks || []), bids: mul(ob.bids || []) };
  }, []);

  const sendSubscribeOBU = useCallback((ws) => {
    const stream = `ob.${symbol}.50`; // 50 уровней
    ws.send(JSON.stringify({
      time: Date.now(),
      channel: "futures.obu",
      event: "subscribe",
      payload: [stream],
    }));
    console.log("📡 [GATE FUTURES] SUB OBU:", stream);
  }, [symbol]);

  const sendUnsubscribeOBU = useCallback((ws) => {
    try {
      const stream = `ob.${symbol}.50`;
      ws?.send(JSON.stringify({
        time: Date.now(),
        channel: "futures.obu",
        event: "unsubscribe",
        payload: [stream],
      }));
      console.log("📴 [GATE FUTURES] UNSUB OBU:", stream);
    } catch {}
  }, [symbol]);

  const sendSubscribeTickers = useCallback((ws) => {
    ws.send(JSON.stringify({
      time: Date.now(),
      channel: "futures.tickers",
      event: "subscribe",
      payload: [symbol],
    }));
    console.log("📡 [GATE FUTURES] SUB TICKERS:", symbol);
  }, [symbol]);

  const sendUnsubscribeTickers = useCallback((ws) => {
    try {
      ws?.send(JSON.stringify({
        time: Date.now(),
        channel: "futures.tickers",
        event: "unsubscribe",
        payload: [symbol],
      }));
      console.log("📴 [GATE FUTURES] UNSUB TICKERS:", symbol);
    } catch {}
  }, [symbol]);

  const applyDeltas = useCallback((updates, side) => {
    const book = [...orderBookRef.current[side]];
    updates.forEach(([price, quantity]) => {
      const pn = +price, qn = +quantity;
      const idx = book.findIndex(([p]) => +p === pn);
      if (qn === 0) {
        if (idx !== -1) book.splice(idx, 1);
      } else {
        if (idx !== -1) book[idx] = [price, quantity];
        else book.push([price, quantity]);
      }
    });
    orderBookRef.current[side] =
      side === "asks"
        ? book.sort((a,b)=>+a[0]-+b[0]).slice(0,20)
        : book.sort((a,b)=>+b[0]-+a[0]).slice(0,20);

    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(() => {
      setOrderBook(scaleBook(orderBookRef.current)); // 👈 масштабируем
    });
  }, [scaleBook]);

  const flushDeltaBuffer = useCallback(() => {
    if (!deltaBufferRef.current.length) return;
    for (const msg of deltaBufferRef.current) {
      const { a = [], b = [] } = msg.result || {};
      if (a.length) applyDeltas(a, "asks");
      if (b.length) applyDeltas(b, "bids");
      if (typeof msg.result?.u === "number") {
        lastUpdateId.current = msg.result.u;
      }
    }
    deltaBufferRef.current.length = 0;
  }, [applyDeltas]);

  const resubscribe = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    hasInitialBook.current = false;
    lastUpdateId.current = null;
    deltaBufferRef.current.length = 0;
    orderBookRef.current = { asks: [], bids: [] };
    setOrderBook({ asks: [], bids: [] });

    sendUnsubscribeOBU(ws);
    setTimeout(() => sendSubscribeOBU(ws), 30);
  }, [sendSubscribeOBU, sendUnsubscribeOBU]);

  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);

      if (data.event === "subscribe" || data.event === "unsubscribe") {
        console.log("✅ [GATE FUTURES]", data.event, "ok:", data.channel);
        return;
      }

      if (data.channel === "futures.tickers" && data.event === "update") {
        const arr = Array.isArray(data.result) ? data.result : [];
        const row = arr.find((r) => r.contract === symbol) || arr[0];
        if (row) {
          setTickerData({
            symbol: row.contract,
            lastPrice: row.last,
            volume24: row.volume_24h,
            fundingRate: row.funding_rate,
            markPrice: row.mark_price,
            indexPrice: row.index_price,
          });
          const fr = parseFloat(row.funding_rate);
          if (!Number.isNaN(fr)) setFundingRate(fr);
        }
        return;
      }

      if (data.channel === "futures.obu" && data.event === "update") {
        const { full, a = [], b = [], U, u } = data.result || {};

        if (full) {
          orderBookRef.current = {
            asks: (a || []).map(([p,q]) => [String(p), String(q)]).sort((x,y)=>+x[0]-+y[0]).slice(0,20),
            bids: (b || []).map(([p,q]) => [String(p), String(q)]).sort((x,y)=>+y[0]-+x[0]).slice(0,20),
          };
          setOrderBook(scaleBook(orderBookRef.current)); // 👈 масштабируем при full
          lastUpdateId.current = typeof u === "number" ? u : null;
          hasInitialBook.current = true;
          flushDeltaBuffer();
          return;
        }

        if (!hasInitialBook.current) {
          deltaBufferRef.current.push(data);
          return;
        }

        if (typeof U === "number" && typeof lastUpdateId.current === "number") {
          if (U !== lastUpdateId.current + 1) {
            console.warn(`⚠️ [GATE FUTURES] gap: expected ${lastUpdateId.current + 1}, got ${U} — resubscribe`);
            resubscribe();
            return;
          }
        }

        if (a.length) applyDeltas(a, "asks");
        if (b.length) applyDeltas(b, "bids");
        if (typeof u === "number") lastUpdateId.current = u;
      }
    } catch (e) {
      console.error("❌ [GATE FUTURES] parse error:", e);
    }
  }, [applyDeltas, flushDeltaBuffer, resubscribe, scaleBook, symbol]);

  useEffect(() => {
    if (!symbol) return;

    hasInitialBook.current = false;
    lastUpdateId.current = null;
    deltaBufferRef.current.length = 0;
    orderBookRef.current = { asks: [], bids: [] };
    setOrderBook({ asks: [], bids: [] });
    setTickerData(null);
    setFundingRate(0);
    setError(null);

    (async () => {
      // получаем quanto_multiplier ДО WS
      contractSizeRef.current = await getFuturesContractSize("gate", symbol);

      const ws = new WebSocket("wss://fx-ws.gateio.ws/v4/ws/usdt");
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setError(null);
        sendSubscribeOBU(ws);
        sendSubscribeTickers(ws);
      };

      ws.onmessage = handleMessage;

      ws.onerror = (err) => {
        console.error("❌ [GATE FUTURES] WS error:", err);
        setError(err.message || "WS error");
        setIsConnected(false);
      };

      ws.onclose = () => {
        console.log("🔌 [GATE FUTURES] WS closed");
        setIsConnected(false);
      };
    })();

    return () => {
      try {
        const ws = wsRef.current;
        if (ws?.readyState === WebSocket.OPEN) {
          sendUnsubscribeTickers(ws);
          sendUnsubscribeOBU(ws);
          ws.close();
        }
      } catch {}
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [symbol, handleMessage, sendSubscribeOBU, sendUnsubscribeOBU, sendSubscribeTickers, sendUnsubscribeTickers]);

  return { isConnected, error, orderBook, tickerData, fundingRate };
};
